import { MicOffOutlined, MicOutlined, Send } from "@mui/icons-material";
import {
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { defaultLocale, locales, logoURL } from "../config";
import useChatbot from "../provider/chatbot_provider";
import Conversation from "./conversation";
import Feedback from "./Feedback/feedback";
import NewChatGreetings from "./new_chat_greetings";
import SuggestionChips from "./suggestions";
import ProfileComponent from "./Profile/profileComponent";
import Logo from "../assets/images/logo.png";
import MenuIcon from "@mui/icons-material/Menu";
import useIdleLogout from "../idleLogout";
import useAuthRedirect from "../userAutoRedirect";
import Mobile_chabotNavbar from "./Navbar/mobile_chabot-navbar";
import { enqueueSnackbar } from "notistack";

let userActivityTimeout: NodeJS.Timeout | null = null;

const checkSession = () => {
  return !!sessionStorage.getItem("userDetails");
};

export default function Chat({ tool }: { tool: string }) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isTabletScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [selectedLocale, setSelectedLocale] = useState(defaultLocale);
  const [query, setQuery] = useState("");
  const {
    chats,
    suggestions,
    selectedChat,
    addConversation,
    conversationsContainerRef,
    like,
    dislike,
    isChatLoading,
  } = useChatbot();
  const { transcript, listening } = useSpeechRecognition();
  const [isUserInactive, setUserInactive] = useState<boolean>(false);
  const [showFeedbackCards, setShowFeedbackCards] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const [sampleSuggestions, setSampleSuggestions] = useState<string[]>([
    "What is the dependent addition time limit (Spouse & Kids)?",
    "What is my relocation allowance?",
    "How to download Employee health card?",
    "What is the eligibility criteria for promotion?",
  ]);
  const [filteredSuggestions, setFilteredSuggestions] =
    useState<string[]>(sampleSuggestions);
  const [showSuggestions, setShowSuggestions] = useState(true);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsError(false);
    const value = event.target.value;
    // setQuery(trimLeadingSpaces(value));
    setQuery(value);
    if (value.length > 0) {
      const newFilteredSuggestions = sampleSuggestions.filter((suggestion) =>
        suggestion.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSuggestions(newFilteredSuggestions);
    } else {
      setFilteredSuggestions(sampleSuggestions);
    }
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    addConversation(suggestion, selectedLocale);
    setQuery("");
    setIsError(false);
  };

  useEffect(() => {
    let q = `${query}${query.length === 0 ? "" : " "}${transcript}`;
    setQuery(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript]);

  useEffect(() => {
    if (chats[selectedChat].conversations.length === 0) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
    if (chats[selectedChat].conversations.length > 0) {
      resetActivityCounter();
    }
    setIsError(false);
  }, [selectedChat]);

  useEffect(() => {
    window.addEventListener("keydown", () => {
      resetActivityCounter();
    });
    return () => {
      if (userActivityTimeout) {
        clearTimeout(userActivityTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    resetActivityCounter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats]);

  function resetActivityCounter() {
    if (tool !== "AI Assistant") return;
    if (userActivityTimeout) {
      clearTimeout(userActivityTimeout);
      if (isUserInactive === true) {
        setUserInactive(false);
      }
    }
    userActivityTimeout = setTimeout(() => {
      setUserInactive(true);
      if (
        chats[selectedChat].conversations.length > 0 &&
        showFeedbackCards === false
      ) {
        setShowFeedbackCards(true);
      }
    }, 60000);
  }

  const [anchorEl, setAnchorEl] = useState<boolean>(false);

  const handleClick = () => {
    setAnchorEl((prevState) => !prevState);
  };

  useIdleLogout();
  const sessionExists = checkSession();
  useAuthRedirect(sessionExists);
  if (!sessionExists) {
    return null;
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter") {
      if (event.shiftKey) {
        return;
      } else {
        event.preventDefault();
        handleSubmit();
      }
    }
  };

  const handleSubmit = () => {
    let trimmedQuery = query.trim();

    if (isChatLoading) {
      enqueueSnackbar(
        "Please wait a moment for the previous question's answer.",
        {
          variant: "warning",
          autoHideDuration: 3000, // 3 seconds
        }
      );

      return;
    }

    // Regular expressions
    const dateRegex =
      /^(?:(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})|(\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2})|(\d{1,2} \w{3,} \d{4}))$/;
    const numberRegex = /^\d+$/;
    const sixDigitNumberRegex = /^\d{6}\s*$/;
    const specialCharOnlyRegex = /^[\s\W]+$/;
    const mixedContentRegex = /^[\d\s\W]+$/;
    const firstCharAlphanumericRegex = /^[a-zA-Z0-9].*$/;

    if (trimmedQuery.length === 0) {
      enqueueSnackbar("Please enter your question before submitting.", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } else if (
      numberRegex.test(trimmedQuery) &&
      !sixDigitNumberRegex.test(trimmedQuery)
    ) {
      enqueueSnackbar(
        `Enter a 6-digit pincode or a valid question. Numbers alone aren't allowed.`,
        { variant: "error", autoHideDuration: 3000 }
      );
    } else if (!firstCharAlphanumericRegex.test(trimmedQuery)) {
      // enqueueSnackbar('The first character must be alphanumeric (a-z, A-Z, 0-9).', { variant: 'error', autoHideDuration: 3000 });
      setIsError(true);
    } else if (
      !sixDigitNumberRegex.test(trimmedQuery) &&
      (numberRegex.test(trimmedQuery) ||
        dateRegex.test(trimmedQuery) ||
        specialCharOnlyRegex.test(trimmedQuery) ||
        mixedContentRegex.test(trimmedQuery))
    ) {
      enqueueSnackbar("Please enter a valid question.", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } else {
      addConversation(trimmedQuery, selectedLocale);
      setQuery("");
    }
  };

  return (
    <Box
      display="flex"
      flexDirection={"column"}
      height="100svh"
      // flex={1}
      position={"relative"}
      overflow={"hidden"}
    >
      <Mobile_chabotNavbar
        tool="someTool"
        hamburger={anchorEl}
        setHamburger={setAnchorEl}
        onClickOutside={() => setAnchorEl(false)}
      />
      {isSmallScreen ? (
        <Box
          position={"absolute"}
          width={"100%"}
          // height={60}
          sx={{
            top: 0,
            margin: "auto 0",
            zIndex: 9999,
            backgroundColor: "white",
            // background:
            //   "linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.2))",
            // backdropFilter: "blur(40px)",
            width: "100%",
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            p={1}
            pb={0.5}
          >
            {!anchorEl ? (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={handleClick}
                sx={{ paddingBottom: 1.3, paddingTop: 0, paddingLeft: 2 }}
              >
                <MenuIcon sx={{ fontSize: "2rem" }} />
              </IconButton>
            ) : (
              <>
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="menu"
                  sx={{ paddingBottom: 1.3, paddingTop: 0, paddingLeft: 2 }}
                >
                  <MenuIcon sx={{ fontSize: "2rem" }} />
                </IconButton>
              </>
            )}
            {!anchorEl && (
              <>
                {/* <a href="/" style={{ textDecoration: "none", color: "inherit" }}>
                  <img
                    src={Logo}
                    style={{ width: 120, height: 40, objectFit: "contain", paddingTop: 10 }}
                  />
                </a> */}
                <Box
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  <img
                    src={Logo}
                    style={{
                      width: 120,
                      height: 40,
                      objectFit: "contain",
                      paddingTop: 10,
                    }}
                  />
                </Box>
                <Box m={1} height={30}>
                  <Divider orientation="vertical" />
                </Box>
              </>
            )}
            <Box flex={1}>{/* <Typography>{tool}</Typography> */}</Box>
            <ProfileComponent />
          </Box>
          <Divider />
        </Box>
      ) : null}
      <Box
        flex={1}
        pt={4}
        sx={{ scrollBehavior: "smooth", overflowY: "scroll" }}
        ref={conversationsContainerRef}
      >
        <Box height={"100%"} display="flex" flexDirection="column" pt={4}>
          <ProfileComponent />
          {chats[selectedChat].conversations.length === 0 ? (
            <NewChatGreetings suggestions={suggestions} tool={tool} />
          ) : (
            <Grid
              container
              spacing={isSmallScreen ? 0 : 2}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Grid item md={10} sm={12} xs={12}>
                {chats[selectedChat].conversations.map(
                  (conversation, index) => {
                    return (
                      <Conversation
                        key={index}
                        like={() => like(index)}
                        dislike={() => dislike(index)}
                        conversation={conversation}
                      />
                    );
                  }
                )}
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
      <Box pb={2} display={"absolute"} bottom={0} left={0}>
        <Grid
          container
          spacing={2}
          justifyContent={"center"}
          alignItems={"center"}
        >
          {chats[selectedChat].conversations.length > 0 &&
          chats[selectedChat].conversations[
            chats[selectedChat].conversations.length - 1
          ].questionSuggestion ? (
            <Grid item md={8} sm={12} xs={12}>
              <Box>
                <SuggestionChips
                  suggestions={
                    chats[selectedChat].conversations[
                      chats[selectedChat].conversations.length - 1
                    ].questionSuggestion || []
                  }
                  gridSpace={4}
                  align={"flex-end"}
                />
              </Box>
            </Grid>
          ) : null}

          {chats[selectedChat].conversations.length > 0 && showFeedbackCards ? (
            <Grid item md={8} sm={12} xs={12}>
              <Box>
                <Grid container spacing={2} alignItems={"flex-end"}>
                  <Grid item>
                    <Feedback
                      onClose={() => {
                        resetActivityCounter();
                        setShowFeedbackCards(false);
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          ) : null}

          <Grid item md={8} sm={12} xs={12}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              {chats[selectedChat].conversations.length === 0 &&
                showSuggestions && (
                  <Box
                    sx={{
                      bottom: "100%",
                      left: 0,
                      right: 0,
                      maxWidth: "100vw",
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: "1%",
                      bgcolor: "background.paper",
                      zIndex: 1000,
                      mb: "0.75vh",
                      padding: !isSmallScreen ? 0 : 1,
                      opacity: 0.9,
                      backgroundColor: "rgba(0, 0, 0, 0)",
                      overflow: "hidden",
                    }}
                  >
                    {sampleSuggestions.map((suggestion, index) => (
                      <Chip
                        key={index}
                        label={suggestion}
                        onClick={() => handleSuggestionClick(suggestion)}
                        sx={{
                          margin: "1px",
                          padding: 0,
                          height: "auto",
                          backgroundColor: "white",
                          fontSize: "0.9rem",
                          border: "1.25px solid rgb(67, 131, 186)",
                        }}
                      />
                    ))}
                  </Box>
                )}
              {isError && (
                <Box display="flex" justifyContent="center" alignItems="center">
                  <Typography
                    color="red"
                    sx={{ padding: "0 5rem 0 1rem", fontSize: "0.8rem" }}
                  >
                    Sorry, I don’t understand emojis, special character, or
                    other language alphabets. You can write in any language
                    using English alphabets.
                  </Typography>
                </Box>
              )}
              <Box
                display={"flex"}
                alignItems={"center"}
                pl={isSmallScreen ? 1 : 0}
              >
                {/* <IconButton size="large" type="submit">
          <AttachFileOutlined />
        </IconButton>
        <Box m={1}></Box> */}
                <Box maxWidth={100}></Box>
                <Box flex={1}>
                  <Box
                    p={1.2}
                    px={isSmallScreen ? 1 : 4}
                    bgcolor={"white"}
                    borderRadius={"24px"}
                    boxShadow={10}
                    display={"flex"}
                    alignItems={"center"}
                  >
                    {!isSmallScreen ? (
                      <Box>
                        {/* <TextField
                          variant="standard"
                          select
                          InputLabelProps={{
                            shrink: true,
                          }}
                          InputProps={{
                            disableUnderline: true,

                            style: {
                              borderRadius: "8px",
                            },
                          }}
                          value={selectedLocale}
                          onChange={(e) => setSelectedLocale(e.target.value)}
                        >
                          {locales.map((locale, index) => (
                            <MenuItem value={locale.code}>
                              {locale.locale}
                            </MenuItem>
                          ))}
                        </TextField> */}
                      </Box>
                    ) : null}
                    <Box flex={1} ml={1} position="relative">
                      <TextField
                        placeholder="Ask a question"
                        autoComplete="off"
                        value={query}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown as React.KeyboardEventHandler}
                        type="text"
                        name="chatbot-query"
                        fullWidth
                        variant="standard"
                        multiline
                        minRows={1}
                        maxRows={4}
                        sx={{
                          borderRadius: 20,
                          zIndex: 999,
                          backgroundColor: "#fff",
                          "& .MuiInputBase-root": {
                            borderRadius: "10px",
                          },
                        }}
                        InputProps={{
                          disableUnderline: true,
                          style: {
                            borderRadius: "10px",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* <Box m={1}></Box> */}
                {/* {!isSmallScreen ? (
                  <IconButton
                    size="large"
                    onClick={() =>
                      listening
                        ? SpeechRecognition.stopListening()
                        : SpeechRecognition.startListening()
                    }
                  >
                    {listening ? <MicOffOutlined /> : <MicOutlined />}
                  </IconButton>
                ) : null} */}
                <IconButton size="large" type="submit" disabled={isChatLoading}>
                  <Send />
                </IconButton>
              </Box>
            </form>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
